import { useCallback, useEffect, useRef, useState } from 'react';

import type { RestTransition } from './restTransition';
import type { LoggerCursor } from './useLoggerCursor';
import type { UseRestTimer } from './useRestTimer';

// The rest IS the transition (MX-5 D8). Logging a set used to move the focus
// immediately and flash a "→ Set 3/4" pill for 1.4s while a rest banner counted
// down somewhere else on screen; the set you had just logged was already behind
// you. Now the focus STAYS on it for the whole rest — verifiable, correctable —
// and moves when the rest ends.
//
// So the advance becomes a two-step: the decision is made at log time, against
// the pre-dispatch draft (which is what makes it correct — see restTransition),
// and it is APPLIED after a short transition window (LG-29, Antenore's spec).
//
// LG-29 — the shape Antenore asked for: log a set and the whole logger locks
// (only the timer controls stay live, see restFrozen). You look at the set you
// just closed for ~2.5s, unable to touch it — that window is what protects the
// measurement (the LG-26 loss was a stray tap on the just-logged set mid-rest).
// Then the focus moves to the NEXT set/exercise in the queue, the rest STILL
// running and STILL locked, so you see what's coming while the clock finishes.
// Nothing there is editable until the rest ends (or you cancel it with the ✕):
// to jump to a different exercise you stop the timer, then restart it from the
// header pill if you want. So the transition fires on a timer, NOT at rest end.
//
// ⚠️ CALLED FROM THE LoggerScreen BODY, ABOVE THE <Modal> — never from inside
// useTimelineLogger, which runs within it. This is the LG-13 rule and it is
// load-bearing here, not tidiness: minimize flips the Modal's visible to false
// and RN unmounts its whole subtree. A `pending` transition held below that
// line is DESTROYED by a minimize, while the rest timer (already hoisted, for
// the same reason) keeps counting — so the countdown would end a minute later
// with nothing left to apply and the focus would simply never advance, silently
// and with no symptom to notice. The cursor lives up here for exactly this
// reason, so the thing that moves it lives beside it.
//
// Why `restArmed` is passed in rather than observed: a rest is not always
// armed. An exercise whose default is 0 means "no rest", and editing a past
// session never rests at all. Inferring that from the timer's state would mean
// waiting to see whether it starts, and a transition that silently never fires
// is exactly the class of bug this slice is most able to introduce. The caller
// knows; it says so.
//
// TM-12 — the freeze is not all-or-nothing any more. A rest armed on a `done`
// transition still protects the card that was just closed (that is the LG-26
// lesson and it holds on the last set too), but it must NOT freeze the roster,
// the feed and the add-exercise button: on `done` the transition blanks the
// focus and RE-OPENS the roster, and the reason a rest is running there at all
// is that the athlete is about to train more. Freezing the way out of an empty
// focus for a full countdown would make the fix worse than the bug it fixes.
// So this hook remembers WHICH kind of transition armed the running rest and
// publishes the two answers separately (Antenore 2026-08-09).

/** How long the "no rest to sit through" caption stays up. Same beat the
 *  retired useFocusAdvanceCue used — long enough to read, short enough not to
 *  linger over the next set. */
const FLASH_CUE_MS = 1400;

/** The locked transition window (LG-29, Antenore: "2, 3 secondi"). The set just
 *  logged stays in front of you, untouchable, for this long; then the focus
 *  hands over to the next set/exercise while the rest keeps running. It is the
 *  gap that both lets you SEE what you did and stops you from editing it, which
 *  is the whole LG-26 protection. Kept below any real rest (an armed rest is the
 *  exercise default, never under a few seconds), so it fires before the count
 *  ends; if a rest somehow ends first, the rest-end path below applies instead. */
const TRANSITION_DELAY_MS = 2500;

export function useRestAdvance({
  restTimer,
  cursor,
}: {
  restTimer: UseRestTimer;
  /** The focus cursor, which this hook is the sole post-log mover of. */
  cursor: LoggerCursor;
}) {
  const [pending, setPending] = useState<RestTransition | null>(null);
  // The caption for an advance that had NO rest behind it (a zero-rest lift).
  // Without it that advance would be signalled by the card's colour flash
  // alone, and colour alone is never allowed to be the only cue — the retired
  // cue hook always paired the flash with text, and dropping the text on this
  // path would have been a quiet a11y regression.
  const [flashCue, setFlashCue] = useState<RestTransition | null>(null);
  // Bumped every time a transition is APPLIED, so the FocusPanel's advance
  // flash still fires on the moment the card actually changes. It used to ride
  // on the retired cue hook's key, which counted the caption instead.
  const [advanceKey, setAdvanceKey] = useState(0);
  // The kind of transition that armed the rest CURRENTLY running (TM-12).
  // Outlives `pending`, which is cleared the moment the transition applies
  // ~2.5s in, while the freeze question stays live for the whole countdown.
  // Null when nothing armed it — which includes a rest started by hand from the
  // header pill, and that case keeps freezing exactly as it did before.
  const [armedBy, setArmedBy] = useState<RestTransition['kind'] | null>(null);

  // Applies a transition — the ONLY place the cursor moves after a log. Runs at
  // the END of the rest, so the set you just logged stays in front of you for
  // the whole countdown: verifiable, and correctable without navigating back.
  // 'set' only clears the manual selection; the re-render then binds the next
  // pending set.
  //
  // 'done' does the SAME, and that is TM-12a (Antenore, smoke of 4.14.2-dev,
  // 2026-08-09). It used to blank the focus and re-open the roster — "point A",
  // his own earlier pick, taken when `done` meant nothing more would happen on
  // screen. TM-12 changed that premise: a rest now runs there, the rest row
  // lives ON the focused card, so blanking the card HID a countdown that was
  // still ticking. His report: «fa il fade per mostrare l'esercizio successivo,
  // ma non c'è… non vedo più il timer, che è in esecuzione però, infatti se
  // tappo sulla card lo vedo.» ⇒ with nothing queued, the focus stays on the
  // exercise just closed. The logged values were always visible in the feed
  // (point A's argument); what was NOT visible is the thing that is running.
  const { setActiveId, setSelectedSetIdx, setRosterExpanded } = cursor;
  const apply = useCallback(
    (transition: RestTransition) => {
      if (transition.kind === 'exercise') {
        setActiveId(transition.entryId);
        setSelectedSetIdx(null);
        setRosterExpanded(false);
      } else {
        setSelectedSetIdx(null);
      }
      setAdvanceKey((k) => k + 1);
    },
    [setActiveId, setSelectedSetIdx, setRosterExpanded],
  );

  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The LG-29 transition timer: fires TRANSITION_DELAY_MS after a log to hand
  // the focus to the next set/exercise, mid-rest. Held in a ref so re-arming or
  // an early rest-end can cancel it before it double-applies.
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTransitionTimer = useCallback(() => {
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
      transitionTimer.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    },
    [],
  );

  const arm = useCallback(
    (transition: RestTransition, restArmed: boolean) => {
      if (!restArmed) {
        // Nothing to sit through: move now, and say so in words as well as in
        // the flash. 'done' carries no caption (there is nowhere to point) and,
        // since TM-12a, moves nothing either — it just stays put.
        setArmedBy(null);
        apply(transition);
        if (transition.kind !== 'done') {
          setFlashCue(transition);
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlashCue(null), FLASH_CUE_MS);
        }
        return;
      }
      // A rest is running. Hold the pending transition for the caption, and arm
      // the timer that will apply it after the locked ~2.5s window (LG-29): the
      // focus hands over to the next set/exercise while the rest keeps running.
      // The captured `transition` is applied directly; the rest-end path below
      // clears this timer if the count somehow finishes first, so a transition
      // is never applied twice.
      setPending(transition);
      setArmedBy(transition.kind);
      clearTransitionTimer();
      transitionTimer.current = setTimeout(() => {
        transitionTimer.current = null;
        apply(transition);
        setPending(null);
      }, TRANSITION_DELAY_MS);
    },
    [apply, clearTransitionTimer],
  );

  // Drop the pending transition WITHOUT applying it, and stop the timer that
  // would. Called when the athlete leaves the resting card by hand — which, with
  // LG-29's lock, only happens AFTER they cancel the rest (the ✕), so the focus
  // they then pick must not be yanked away by a queued hop. The countdown, if
  // any is still running, keeps going as a clock; it just no longer commands the
  // focus.
  const cancel = useCallback(() => {
    clearTransitionTimer();
    setPending(null);
  }, [clearTransitionTimer]);

  // A rest is over when the countdown reaches 0 (`finished`, the moment the cue
  // fires — NOT the end of the 3s grace window, which only ever existed to let
  // the old banner fade out) or when it is cleared, which is the ✕ on the
  // meter's column and the reset that re-arming performs.
  const { endsAt, isPaused, finished } = restTimer.state;
  const resting = (endsAt !== null && !finished) || isPaused;
  const wasResting = useRef(false);
  useEffect(() => {
    const was = wasResting.current;
    wasResting.current = resting;
    if (!was || resting) return;
    // The rest ended (finished, or ✕). Normally the transition already fired on
    // its ~2.5s timer well before this; but a rest shorter than the window, or a
    // skip within it, ends here first. Cancel the pending timer so it can't
    // double-apply, then apply the transition if it is still waiting.
    clearTransitionTimer();
    setPending((current) => {
      if (current !== null) apply(current);
      return null;
    });
    // The rest that was running is gone, so the question "who armed it" has no
    // subject any more. A rest started by hand next gets the null answer, i.e.
    // the pre-TM-12 freeze.
    setArmedBy(null);
  }, [resting, apply, clearTransitionTimer]);

  return {
    /** The transition being waited out, for the rest row's caption. Null when
     *  no rest is running or when the rest was started by hand from the header
     *  (a manual rest transitions to nothing). */
    pending,
    /** A just-applied transition with no rest behind it, for the transient
     *  caption on the card. Clears itself after a beat. */
    flashCue,
    /** A rest is counting. Under LG-29 this IS the condition, both for showing
     *  the rest row and for the card lock (restFrozen): while a rest runs the
     *  focused card is frozen, so the focus can only be where the transition
     *  put it, and the rest row rides along — there is no unrelated card to
     *  guard against anymore (the old ownership check in useTimelineLogger is
     *  gone). */
    resting,
    /** Whether this rest also freezes the NAVIGATION surfaces — roster, feed,
     *  add-exercise (TM-12). False for a rest armed on `done`: there the focus
     *  has blanked and the roster has re-opened, and the athlete's next move is
     *  to add a set or an exercise. Everything else, a manual header rest
     *  included, freezes as before. */
    freezesNavigation: resting && armedBy !== 'done',
    arm,
    cancel,
    advanceKey,
  };
}
